import { useState, useEffect } from 'react';
import { RefreshCw, Plus } from 'lucide-react';
import DeviceList from './components/DeviceList';
import ScanJobList from './components/ScanJobList';
import NewScanModal from './components/NewScanModal';
import AddDeviceModal from './components/AddDeviceModal';
import { scannerAPI } from '../../services/scannerService';
import { ScannerDevice, ScanJob } from '../../types/scanner';
import toast from 'react-hot-toast';

export default function ScannerPage() {
  const [devices, setDevices] = useState<ScannerDevice[]>([]);
  const [jobs, setJobs] = useState<ScanJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewScanModal, setShowNewScanModal] = useState(false);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<ScannerDevice | null>(null);

  useEffect(() => {
    loadData();
    // Poll for updates every 5 seconds
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [devicesData, jobsData] = await Promise.all([
        scannerAPI.getDevices(),
        scannerAPI.getJobs({ limit: 20 })
      ]);
      setDevices(devicesData);
      setJobs(jobsData);
    } catch (error) {
      console.error('Failed to load scanner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewScan = (device: ScannerDevice) => {
    setSelectedDevice(device);
    setShowNewScanModal(true);
  };

  const handleScanCreated = () => {
    setShowNewScanModal(false);
    loadData();
    toast.success('Scan started successfully!');
  };

  const handleDeviceCreated = () => {
    setShowAddDeviceModal(false);
    loadData();
    toast.success('Scanner device added successfully!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">HDScanner Control</h1>
        <p className="text-gray-600 mt-2">
          Manage scanner devices and scan jobs
        </p>
      </div>

      {/* Devices Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Scanner Devices</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddDeviceModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              <Plus className="w-4 h-4" />
              Add Device
            </button>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
        <DeviceList devices={devices} onNewScan={handleNewScan} />
      </div>

      {/* Jobs Section */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Recent Scan Jobs</h2>
        <ScanJobList jobs={jobs} onRefresh={loadData} />
      </div>

      {/* New Scan Modal */}
      {showNewScanModal && (
        <NewScanModal
          device={selectedDevice}
          onClose={() => setShowNewScanModal(false)}
          onSuccess={handleScanCreated}
        />
      )}

      {/* Add Device Modal */}
      {showAddDeviceModal && (
        <AddDeviceModal
          onClose={() => setShowAddDeviceModal(false)}
          onSuccess={handleDeviceCreated}
        />
      )}
    </div>
  );
}
